import { escapeHtml } from 'shared-helpers/common';
import { getMessageWithPlaceholders } from 'shared-locales';
import { newlinesToLineBreak } from 'lib/helpers';
import { useReduxState } from 'hooks';
import { Avatar, User } from 'components/common';
import { EditedContent } from 'components/common/notifications';
import { ReviewDropdown, ReviewStars } from '.';

import type { TaskAnswerReview } from 'shared-types';

interface Props {
  courseId: number;
  isAdmin?: boolean;
  onEdit?: () => void;
  onDataChange?: () => void;
  review: TaskAnswerReview;
}

const Review = ({ courseId, isAdmin, onEdit, onDataChange, review }: Props) => {
  const { messages, theme, user: loggedInUser } = useReduxState();

  return (
    <div className="review">
      <div className="header">
        <div className="type-label">
          {review.type === 'review'
            ? messages.tasksReview
            : review.type === 'request-changes'
            ? messages.tasksChangesRequested
            : messages.commonComment}
        </div>
        {isAdmin ? (
          <div className="dropdown">
            <ReviewDropdown
              courseId={courseId}
              onEdit={onEdit}
              onDataChange={onDataChange}
              review={review}
            />
          </div>
        ) : null}
      </div>
      <div className="user">
        <User
          enableProfileLink={loggedInUser?.roles?.includes('admin')}
          showEmailTooltip={isAdmin}
          size="small"
          user={review.user}
          subtitle={
            <EditedContent
              createdAt={review.createdAt}
              updatedAt={review.updatedAt}
            />
          }
        />
      </div>

      <div
        className="text"
        dangerouslySetInnerHTML={{
          __html: newlinesToLineBreak(escapeHtml(review.data.text))
        }}
      />

      {review.data.points !== undefined ? (
        <div className="points">
          <Avatar
            fontSize="1.4rem"
            padding="2px"
            size="23px"
            style={{ fontWeight: 700 }}
          >
            {review.data.points.toString()}
          </Avatar>
          {getMessageWithPlaceholders({
            message: messages.commonNumberOfPoints,
            placeholders: { numberOfPoints: '' }
          })}
        </div>
      ) : review.data.stars ? (
        <div className="stars">
          <ReviewStars value={review.data.stars} />
        </div>
      ) : null}

      <style jsx>{`
        .review {
          background-color: white;
          border-radius: ${theme.cardBorderRadius};
          padding: 15px;
        }
        .review:not(:first-of-type) {
          margin-top: 15px;
        }
        .header {
          align-items: center;
          display: flex;
          margin-bottom: 5px;
        }
        .type-label {
          flex-grow: 1;
          font-size: 1.4rem;
          font-weight: 600;
          margin-bottom: 10px;
        }
        .user {
          margin-bottom: 10px;
        }
        .dropdown {
          margin-left: 15px;
        }
        .text {
          color: ${theme.textColor};
          font-size: 1.6rem;
        }
        .points {
          align-items: center;
          color: ${theme.textColorLight};
          display: flex;
          font-size: 1.4rem;
          font-weight: 600;
          gap: 5px;
          margin-top: 15px;
        }
        .stars {
          color: ${theme.textColorLight};
          font-size: 1.3rem;
          margin-top: 15px;
        }
      `}</style>
    </div>
  );
};

export default Review;
